import { Box, GlobalStyles, Typography, useTheme } from "@mui/material";
import {
	CoreButton,
	CoreCard,
	CoreDatePicker,
	CoreSearchField,
	CoreSearchFieldPaper,
	coreToast,
} from "@verity/components/core";
import { scrollbarThemeOverrides, ThemeWrapper } from "@verity/components";
import { useContext, useEffect, useId, useRef, useState } from "react";
import VisualizerBanner from "../portal/components/visualizer-banner";
import i18n from "../i18n";
import { registerPlugin, fetchUsageReport, generateHistoricEmailReport, generateEmailPDF } from "./shared/helpers";
import { NoResults } from "./shared/report-components";
import { InstitutionReportEmailData } from "./shared/report-types";
import ArrowRight from "../images/reports/arrowRight.svg?react";
import SwitchIcon from "../images/reports/switch.svg?react";
import BackButton from "../components/back-button.tsx";
import DownloadIcon from "../results/components/Resources/download.svg?react";
import MegaphoneIcon from "../../../../packages/components/src/core/notification-modal/Megaphone.svg?react";
import { PortalControllerContext } from "../portal/context";
import { canAccessResource } from "../helpers/privilege";
import { UsageReportCardSkeleton, EmailContentReportSkeleton } from "./shared/usage-report-card-skeleton.tsx";
import PortalBanner from "../portal/components/portal-banner.tsx";

function EmailList({
	filteredEmails,
	handleViewReport,
}: {
	filteredEmails: InstitutionReportEmailData[];
	handleViewReport: (email: InstitutionReportEmailData) => void;
}) {
	const theme = useTheme();
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "20px",
				flexShrink: 1,
				minHeight: 0,
			}}
		>
			{filteredEmails.map((email) => (
				<CoreCard
					key={`${email.year}-${email.month}`}
					style={{
						padding: "15px",
						borderRadius: "6px",
						borderWidth: "0.6px",
						borderColor: "#D9D9F7",
						opacity: "1",
						background: "linear-gradient(96.04deg, #FFFFFF 1.99%, #F2F1FF 93.82%, #F4EFFF 115.69%)",
					}}
				>
					<Box
						display="flex"
						alignItems="flex-start"
						justifyContent="center"
						flexWrap="wrap"
						sx={{
							flexDirection: "row",
							[theme.breakpoints.down(1050)]: {
								flexDirection: "column",
							},
						}}
					>
						{/* LEFT IMAGE SECTION */}
						<Box
							sx={{
								flex: "0 0 250px",
								width: "250px",
								height: "250px",
								alignSelf: { xs: "center", lg: "stretch" },
								borderRadius: 8,
								opacity: 1,
								padding: "10px 50px 10px 25px",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							{email.topMetrics[0].type !== "absolute" &&
							email.topMetrics[0].type !== "absolute-csm" &&
							email.topMetrics[0].imageUrl ? (
								<Box
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
									width="200px"
									height="210px"
									textAlign="center"
								>
									<img
										src={email.topMetrics[0].imageUrl}
										alt={i18n.t("instructor.components.reports.ariaLabels.usageReportChart")}
										style={{
											width: "130%",
											maxWidth: "230px",
											height: "auto",
											objectFit: "contain",
										}}
									/>
								</Box>
							) : (
								<Box
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
									width="200px"
									height="210px"
									paddingLeft="10px"
									textAlign="center"
									sx={{
										fontSize: "25px",
										fontWeight: 600,
										color: "#2E2E2E",
										margin: "0 auto",
									}}
								>
									<div style={{ marginBottom: "4px" }}>
										{new Date(email.year, email.month - 1).toLocaleString(i18n.language, {
											month: "long",
											year: "numeric",
										})}
									</div>
									<div
										style={{
											fontWeight: "bold",
											fontSize: "22px",
											background: "linear-gradient(to right, #0077B6, #00B4D8)",
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
										}}
									>
										{i18n.t("instructor.components.reports.usage.reportImagePlaceholder")}
									</div>
								</Box>
							)}
						</Box>

						{/* RIGHT TEXT SECTION */}
						<Box
							display="flex"
							flexDirection="column"
							justifyContent={{ xs: "flex-end", lg: "center" }}
							alignItems="flex-start"
							gap={2}
							flex="1"
							textAlign="left"
							sx={{
								width: "100%",
								maxWidth: "100%",
								overflowWrap: "break-word",
								wordBreak: "break-word",
							}}
						>
							<div
								style={{
									width: "100%",
									maxWidth: "100%",
									fontFamily: "Inter",
									fontWeight: 600,
									fontStyle: "normal",
									fontSize: "20px",
									lineHeight: "140%",
									letterSpacing: "0px",
									verticalAlign: "middle",
									color: "var(--Primary, #42296E)",
									overflowWrap: "break-word",
								}}
							>
								{new Date(email.year, email.month - 1).toLocaleString(i18n.language, {
									month: "long",
									year: "numeric",
								})}{" "}
								- {email.title || i18n.t("instructor.components.reports.usage.reportTitlePlaceholder")}
							</div>

							<div
								style={{
									width: "100%",
									maxWidth: "100%",
									fontFamily: "Inter",
									fontWeight: 400,
									fontStyle: "normal",
									fontSize: "15px",
									lineHeight: "150%",
									letterSpacing: "0px",
									color: "#191919",
									overflowWrap: "break-word",
								}}
							>
								{email.summary}
							</div>

							<CoreButton onClick={() => handleViewReport(email)}>
								<Typography
									style={{
										fontFamily: "Inter",
										fontWeight: 400,
										fontStyle: "normal",
										fontSize: "15px",
										lineHeight: "150%",
										letterSpacing: "0px",
										textAlign: "center",
									}}
								>
									{i18n.t("instructor.components.reports.usage.viewReport")}
								</Typography>
							</CoreButton>
						</Box>
					</Box>
				</CoreCard>
			))}
		</Box>
	);
}

function CSMBanner({ isCsmViewMode }: { isCsmViewMode: boolean }) {
	return (
		<>
			{isCsmViewMode && (
				<PortalBanner
					style={{
						width: "auto",
						border: "2px solid #c0b9ec",
						background: "#f9f8ff",
						boxShadow: "none",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
						<MegaphoneIcon
							style={{
								width: 24,
								height: 24,
								color: "#42296E",
								flexShrink: 0,
							}}
						/>
						<strong>{i18n.t("instructor.components.reports.usage.csmBannerTitle")}</strong>
						<span>{i18n.t("instructor.components.reports.usage.csmBannerDesc")}</span>
					</Box>
				</PortalBanner>
			)}
		</>
	);
}

export function UsageReport({ instituteId }: { instituteId: number }) {
	const { userPrivileges, portalContext } = useContext(PortalControllerContext);
	const { instituteUuid } = portalContext;
	const [loading, setLoading] = useState<boolean>(false);
	const [isCsmViewMode, setIsCsmViewMode] = useState<boolean>(false);
	const canEditVeritySettings = canAccessResource(
		{ privileges: userPrivileges },
		{
			privilege: "INSTITUTE/FEATURE",
			action: "UPDATE",
			resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
		},
	);

	const [usageEmailsData, setUsageEmailsData] = useState<Array<InstitutionReportEmailData>>();
	const [selectedEmail, setSelectedEmail] = useState<InstitutionReportEmailData | undefined>(undefined);
	const [showingReportDetails, setShowingReportDetails] = useState<boolean>(false);
	const [emailContent, setEmailContent] = useState<string>("");
	const [searchTerm, setSearchTerm] = useState<string>("");
	const controlId = useId();

	const [startDate, setStartDate] = useState(() => {
		const today = new Date();
		const defaultDate = new Date(today.getFullYear() - 1, today.getMonth() - 1); // one year ago
		return {
			month: defaultDate.getMonth() + 1, // 1–12
			year: defaultDate.getFullYear(),
		};
	});
	const [endDate, setEndDate] = useState(() => {
		const date = new Date();
		return {
			month: date.getMonth() + 1, // 1–12
			year: date.getFullYear(),
		};
	});
	const firstLoadRef = useRef(true);
	const [noEmailsOnFirstLoad, setNoEmailsOnFirstLoad] = useState(false);

	const filteredEmails = usageEmailsData?.filter((email) => {
		const search = searchTerm.trim().toLowerCase();
		if (!search) return true;

		// Create a proper month name string
		const title = `${new Date(email.year, email.month - 1).toLocaleString("default", {
			month: "long",
		})} ${email.year}`.toLowerCase();

		const summary = email.summary?.toLowerCase() ?? "";
		const reportTitle = email.title?.toLowerCase() ?? "";

		// Match against multiple fields
		return title.includes(search) || summary.includes(search) || reportTitle.includes(search);
	});

	const handleViewReport = async (email: InstitutionReportEmailData) => {
		setLoading(true);
		setSelectedEmail(email);
		setShowingReportDetails(true);
		try {
			const htmlContent = await generateHistoricEmailReport(email, isCsmViewMode);
			setEmailContent(htmlContent?.html ?? "");
			setLoading(false);
		} catch (error) {
			setEmailContent("<p>Error loading report content</p>");
			setLoading(false);
		}
	};

	const handleDownloadPdf = async (html: string, month: number, year: number) => {
		const blob = await generateEmailPDF(html);
		if (!blob) return;

		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `UsageReport_${month}_${year}.pdf`;
		link.click();

		URL.revokeObjectURL(url);
	};

	registerPlugin();
	useEffect(() => {
		setLoading(true);

		void (async () => {
			if (startDate.year > endDate.year || (startDate.year === endDate.year && startDate.month > endDate.month)) {
				setLoading(false);
				return;
			}

			try {
				const report = await fetchUsageReport({
					instituteId,
					startDate,
					endDate,
					isCSM: canEditVeritySettings,
				});

				if (report?.length === 0 && firstLoadRef.current) {
					setNoEmailsOnFirstLoad(true);
				} else {
					setNoEmailsOnFirstLoad(false);
				}
				setUsageEmailsData(report ?? []);
			} catch (err) {
				coreToast.info(i18n.t("instructor.components.reports.usage.getReportsFailed"), {
					toastId: "report-missing",
				});
			} finally {
				setLoading(false);
				firstLoadRef.current = false; // mark first load as done
			}
		})();
	}, [startDate, endDate, instituteId]);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				height: "100%",
				background: "#F7F8FA",
				overflow: "hidden",
			}}
		>
			<GlobalStyles styles={scrollbarThemeOverrides} />
			{!showingReportDetails ? (
				<>
					<Box
						sx={{
							"&:not(:empty)": {
								display: "flex",
								width: "100%",
								maxWidth: "100%",
								padding: "10px",
								paddingTop: "0px",
								marginTop: "10px",
								boxSizing: "border-box",
							},
						}}
					>
						<VisualizerBanner />
					</Box>
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							padding: "10px",
							boxSizing: "border-box",
							overflow: "hidden",
							minHeight: 0,
						}}
					>
						<Box
							style={{
								display: "flex",
								flexDirection: "column",
								padding: "10px",
								flex: 1,
								minHeight: 0,
								boxSizing: "border-box",
								overflow: "hidden",
								borderRadius: "12px",
								backgroundColor: "#ffffff",
								boxShadow: "0 0 5px 0 rgba(0,0,0,0.1)",
							}}
						>
							<Box id="top-box" sx={{ flex: "0 0 auto" }}>
								{/** Verity Usage Report Header */}
								<CSMBanner isCsmViewMode={isCsmViewMode} />
								<Box sx={{ display: "flex", flexDirection: "column", padding: "15px 15px 25px 15px", width: "auto" }}>
									{/* Title Row */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											width: "100%",
											gap: "15px",
											flexDirection: {
												xs: "column",
												md: "row",
											},
										}}
									>
										{/* LEFT GHOST SLOT (matches button width automatically) */}
										<Box sx={{ display: { xs: "none", md: "block" }, visibility: "hidden" }}>
											<CoreButton />
										</Box>

										{/* CENTER TITLE */}
										<Box sx={{ flex: 1, textAlign: "center" }}>
											<span
												style={{
													fontWeight: 700,
													fontSize: "24px",
													lineHeight: "140%",
													color: "var(--Primary, #42296E)",
												}}
											>
												{i18n.t("instructor.components.reports.usage.title")}
											</span>
										</Box>

										{canEditVeritySettings && ( // canEditVeritySettings
											<CoreButton
												color="primary"
												onClick={() => setIsCsmViewMode(!isCsmViewMode)}
												style={{ padding: "5px" }}
											>
												<SwitchIcon style={{ paddingRight: "4px" }} />
												{isCsmViewMode
													? i18n.t("instructor.components.reports.usage.switchtoAdmin")
													: i18n.t("instructor.components.reports.usage.switchtoCSM")}
											</CoreButton>
										)}
									</Box>
								</Box>

								{/** Search Bar & Date Picker */}
								<ThemeWrapper>
									<CoreSearchFieldPaper
										regShadow
										sx={{
											flex: "0 0 auto",
											display: "flex",
											flexDirection: { xs: "column", lg: "row" },
											padding: { xs: "10px 75px", lg: "10px 150px" },
											boxSizing: "border-box",
											overflow: "hidden",
											gap: 2,
										}}
									>
										<Box
											sx={{
												flex: 1,
												minWidth: 0,
												width: "100%",
											}}
										>
											<CoreSearchField
												onSearch={setSearchTerm}
												label={i18n.t("instructor.components.reports.usage.searchReportPlaceHolder")}
												totalResults={filteredEmails?.length ?? 0}
												ariaControls={controlId}
												value={searchTerm}
												showClearButton
											/>
										</Box>

										<Box
											sx={{
												display: "flex",
												flexDirection: "row",
												alignItems: "center",
												gap: 1,
												flexWrap: "wrap",
												flexShrink: 0,
												width: { xs: "100%", lg: "auto" },
											}}
										>
											<Typography fontWeight={400} fontSize={"15px"}>
												{i18n.t("portal.components.assessmentDecoyModalTSX.filterDate")}
											</Typography>
											<CoreDatePicker
												format="MMMM yyyy"
												disableFuture
												defaultValue={new Date(startDate.year, startDate.month)}
												views={["year", "month"]}
												onMonthChange={(date) => {
													const month = date.getMonth() + 1; // 1–12
													const year = date.getFullYear();
													setStartDate({ month, year });
												}}
												dialogAriaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarStartDialog")}
												ariaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarStartInput")}
												useButton
												slotProps={{
													textField: {
														InputProps: {
															sx: {
																height: "36px",
																maxWidth: "185px",
																"&&& input": { fontSize: "15px" },
															},
														},
													},
													popper: { placement: "bottom-start" },
												}}
											/>
											<ArrowRight />
											<CoreDatePicker
												format="MMMM yyyy"
												disableFuture
												views={["year", "month"]}
												defaultValue={new Date()}
												onMonthChange={(date) => {
													const month = date.getMonth() + 1; // 1–12
													const year = date.getFullYear();
													setEndDate({ month, year });
												}}
												dialogAriaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarEndDialog")}
												ariaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarEndInput")}
												minDate={new Date(startDate.year, startDate.month - 1)}
												useButton
												slotProps={{
													textField: {
														InputProps: {
															sx: {
																height: "36px",
																maxWidth: "185px",
																"&&& input": { fontSize: "15px" },
															},
														},
													},
													popper: { placement: "bottom-start" },
												}}
											/>
										</Box>
									</CoreSearchFieldPaper>
								</ThemeWrapper>
							</Box>

							<Box
								id="bottom-box"
								sx={{
									marginTop: "20px",
									flex: 1,
									minHeight: 0,
									overflowY: "auto",
								}}
							>
								{/** Email Report Table */}
								{loading && (
									<Box className="usage-report-list">
										<UsageReportCardSkeleton count={3} />
									</Box>
								)}

								{!loading && filteredEmails && filteredEmails.length === 0 ? (
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
											minHeight: "100%",
										}}
									>
										<NoResults
											text={
												noEmailsOnFirstLoad
													? i18n.t("instructor.components.reports.usage.noResult.noEmailDataTitle")
													: i18n.t("instructor.components.reports.usage.noResult.noMatchingResultTitle")
											}
											description={
												noEmailsOnFirstLoad
													? i18n.t("instructor.components.reports.usage.noResult.noEmailDataDesc")
													: searchTerm === ""
														? i18n.t("instructor.components.reports.usage.noResult.noMatchingResultDateDesc")
														: i18n.t("instructor.components.reports.usage.noResult.noMatchingResultSearchDesc")
											}
											emptyEmail={noEmailsOnFirstLoad}
											add={true}
										/>
									</Box>
								) : (
									<EmailList filteredEmails={filteredEmails ?? []} handleViewReport={handleViewReport} />
								)}
							</Box>
						</Box>
					</Box>
				</>
			) : (
				selectedEmail && (
					<>
						<Box
							sx={{
								padding: "10px",
								display: "flex",
								flexDirection: "column",
								flex: 1,
								minHeight: 0,
								overflow: "hidden",
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									padding: "10px",
									flex: 1,
									minHeight: 0,
									boxSizing: "border-box",
									overflow: "hidden",
									borderRadius: "12px",
									backgroundColor: "#ffffff",
									boxShadow: "0 0 5px 0 rgba(0,0,0,0.1)",
								}}
							>
								{/* CSM Banner */}
								<CSMBanner isCsmViewMode={isCsmViewMode} />

								<Box id="top-box" sx={{ flex: "0 0 auto" }}>
									<Box display="flex" justifyContent="space-between" alignItems="center" paddingTop="10px">
										{/* Left side: Back button */}
										<Box display="flex" alignItems="center">
											<BackButton
												onBack={() => {
													setShowingReportDetails(false);
													setSelectedEmail(undefined);
													setEmailContent("");
												}}
												ariaLabel={i18n.t("portal.components.createAssessmentPageTSX.backButtonAriaLabel")}
											/>
										</Box>

										<Box display="flex" gap={1}>
											<CoreButton
												color="primary"
												onClick={() => void handleDownloadPdf(emailContent, selectedEmail.month, selectedEmail.year)}
												startIcon={<DownloadIcon />}
											>
												{i18n.t("instructor.components.reports.usage.download")}
											</CoreButton>
										</Box>
									</Box>
								</Box>
								<Box
									sx={{
										marginTop: "20px",
										flex: 1,
										minHeight: 0,
										overflowY: "auto",
									}}
								>
									{loading && <EmailContentReportSkeleton metricsCount={5} />}
									{!loading && (
										<div
											tabIndex={-1}
											dangerouslySetInnerHTML={{
												__html: emailContent,
											}}
										/>
									)}
								</Box>
							</Box>
						</Box>
					</>
				)
			)}
		</div>
	);
}
